// frontend/components/settings/EditMemberModal.tsx

import { useState, useEffect } from "react";
import { X, Save, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { ALL_DEPARTMENTS } from "../../constants/organization";

const POSITIONS = ["사원", "주임", "대리", "과장", "차장", "부장", "이사", "상무", "전무", "대표이사"];

interface Member {
  id: string;
  loginId: string;
  name: string;
  department: string | null;
  position: string | null;
  isDeptHead: boolean;
  role: string;
}

interface EditMemberModalProps {
  member: Member | null;
  onClose: () => void;
  onSave: (memberId: string, data: { department?: string; position?: string; isDeptHead?: boolean }) => Promise<boolean>;
  onResetPassword: (loginId: string) => void;
  onDeleteMember: (memberId: string, loginId: string) => Promise<void>;
}

export default function EditMemberModal({ member, onClose, onSave, onResetPassword, onDeleteMember }: EditMemberModalProps) {
  const [department, setDepartment] = useState("");
  const [position, setPosition]     = useState("");
  const [isDeptHead, setIsDeptHead] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saveMsg, setSaveMsg]       = useState("");

  useEffect(() => {
    if (member) {
      setDepartment(member.department || "");
      setPosition(member.position || "");
      setIsDeptHead(member.isDeptHead || false);
      setSaveMsg("");
    }
  }, [member]);

  if (!member) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onSave(member.id, { department, position, isDeptHead });
    setSaving(false);
    if (ok) {
      setSaveMsg("저장되었습니다.");
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleResetPassword = () => {
    onResetPassword(member.loginId);
    onClose();
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-700 rounded-lg outline-none focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500 text-gray-800 dark:text-zinc-100";
  const labelCls = "block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1";

  return (
    <div className="absolute inset-0 bg-white dark:bg-zinc-900 z-10 flex flex-col animate-in fade-in zoom-in-95 duration-200">
      {/* 헤더 */}
      <div className="flex items-center justify-between border-b dark:border-zinc-800 pb-3 mb-4">
        <p className="text-base font-bold text-gray-800 dark:text-zinc-200 flex items-center gap-2">
          <Pencil size={16} className="text-[#006DBA]" />
          멤버 정보 수정
        </p>
        <button onClick={onClose} className="text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800 p-1 rounded-md">
          <X size={18} />
        </button>
      </div>

      <form onSubmit={handleSave} className="flex-1 space-y-4">
        {/* 읽기 전용: 계정 기본 정보 */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-zinc-800/40 rounded-lg border border-gray-100 dark:border-zinc-800">
          <div>
            <p className={labelCls}>아이디</p>
            <p className="text-sm font-mono text-[#006DBA] dark:text-[#3399FF] font-medium">{member.loginId}</p>
          </div>
          <div>
            <p className={labelCls}>이름</p>
            <p className="text-sm font-bold text-gray-800 dark:text-zinc-100">{member.name || "-"}</p>
          </div>
          <div>
            <p className={labelCls}>권한</p>
            <p className="text-sm text-gray-600 dark:text-zinc-300">
              {member.role === "USER" ? "내부직원" : member.role === "GUEST" ? "외부인" : "최고관리자"}
            </p>
          </div>
        </div>

        {/* 수정 가능 필드 */}
        <div>
          <label className={labelCls}>소속 부서</label>
          <select value={department} onChange={e => setDepartment(e.target.value)} className={inputCls}>
            <option value="">선택 안 함</option>
            {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <label className={labelCls}>직책</label>
          <div className="flex items-center gap-3">
            <select value={position} onChange={e => setPosition(e.target.value)} className={`${inputCls} flex-1`}>
              <option value="">선택 안 함</option>
              {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-zinc-300 whitespace-nowrap cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isDeptHead}
                onChange={e => setIsDeptHead(e.target.checked)}
                className="w-3.5 h-3.5 accent-blue-500"
              />
              부서장
            </label>
          </div>
        </div>

        {/* 저장 버튼 */}
        <button
          type="submit"
          disabled={saving}
          className="w-full flex items-center justify-center gap-1.5 py-2 bg-[#006DBA] hover:bg-[#005A9E] disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Save size={14} />{saving ? "저장 중…" : "저장"}
        </button>
        {saveMsg && <p className="text-xs text-center text-blue-500 dark:text-blue-400">{saveMsg}</p>}
      </form>

      {/* 위험 구역 — 두 영역으로 분할: 비밀번호 초기화 | 계정 삭제 */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-zinc-800">
        <p className="text-xs font-medium text-gray-400 dark:text-zinc-500 mb-2">위험 구역</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleResetPassword}
            className="flex items-center justify-center gap-1.5 py-2 border border-red-200 dark:border-red-800 text-red-500 dark:text-red-400 text-xs font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
          >
            <RefreshCw size={13} />비밀번호 강제 초기화
          </button>
          <button
            type="button"
            onClick={() => {
              if (!member) return;
              if (!confirm(`[${member.name || member.loginId}] 계정을 완전히 삭제합니다.\n\n• 팀 스페이스 문서는 부서장에게 인계됩니다.\n• 개인 문서는 관리자 인계 페이지로 이동됩니다.\n\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?`)) return;
              onDeleteMember(member.id, member.loginId).then(() => onClose()).catch(() => {});
            }}
            className="flex items-center justify-center gap-1.5 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Trash2 size={13} />계정 삭제
          </button>
        </div>
      </div>
    </div>
  );
}
